import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ToastRegion } from '../components/ui';

/**
 * Application-wide toasts, rendered by the `Toast`/`ToastRegion` primitives
 * (spec §7.2, §12).
 *
 * What this provider owns beyond the primitives:
 *   - politeness: neutral/success/signal toasts are announced politely,
 *     `error` toasts assertively (ToastRegion splits them into two regions —
 *     a live region cannot change its own politeness after the fact),
 *   - an optional action button, which is what makes the "Einprägen"-undo
 *     possible without a modal,
 *   - a queue capped at three, because neither a screen reader nor a reader
 *     can usefully be handed a taller stack of announcements,
 *   - auto-dismiss timers that are cleared on unmount and on manual dismiss.
 *
 * The `useToast()` API shape is unchanged: `toast.error/success/info/warning`
 * still take a message string as the first argument. Everything new is opt-in
 * via a second options argument.
 */
const ToastContext = createContext(null);

/** Spec §7.2: never more than three at once. */
const MAX_TOASTS = 3;

const DEFAULT_DURATION = { error: 6000, warning: 5000, info: 4000, success: 3000 };

/** `type` is the caller-facing vocabulary; `tone` is what the primitive styles on. */
const TONE_BY_TYPE = { error: 'danger', success: 'success', warning: 'signal', info: undefined };

let toastId = 0;

export function ToastProvider({ children }) {
  const { t } = useTranslation();
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef(new Map());

  const clearTimer = useCallback((id) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const dismiss = useCallback(
    (id) => {
      clearTimer(id);
      setToasts((prev) => prev.filter((item) => item.id !== id));
    },
    [clearTimer]
  );

  const dismissAll = useCallback(() => {
    timersRef.current.forEach((timer) => clearTimeout(timer));
    timersRef.current.clear();
    setToasts([]);
  }, []);

  /**
   * @param {import('react').ReactNode} message
   * @param {object} [options]
   * @param {'error'|'success'|'info'|'warning'} [options.type='info']
   * @param {import('react').ReactNode} [options.title]
   * @param {number} [options.duration] - 0 keeps the toast until dismissed.
   * @param {{label: string, onClick: () => void, keepOpen?: boolean}} [options.action]
   * @returns {number} the toast id, for `dismiss(id)`.
   */
  const show = useCallback(
    (message, options = {}) => {
      const { type = 'info', title, action, duration } = options;
      const id = ++toastId;
      // An actionable toast must outlive the default reading time — an undo
      // the user never gets to click is worse than no undo at all.
      const ttl = duration ?? (action ? 8000 : DEFAULT_DURATION[type] ?? 4000);

      const entry = {
        id,
        type,
        tone: TONE_BY_TYPE[type],
        title,
        message,
        action: action
          ? {
              label: action.label,
              onClick: () => {
                action.onClick?.();
                if (!action.keepOpen) dismiss(id);
              }
            }
          : undefined
      };

      setToasts((prev) => {
        const next = [...prev, entry];
        // Oldest first out, so the newest message is always the one on screen.
        const dropped = next.slice(0, Math.max(0, next.length - MAX_TOASTS));
        dropped.forEach((item) => clearTimer(item.id));
        return next.slice(-MAX_TOASTS);
      });

      if (ttl > 0) {
        timersRef.current.set(
          id,
          setTimeout(() => dismiss(id), ttl)
        );
      }

      return id;
    },
    [dismiss, clearTimer]
  );

  useEffect(
    () => () => {
      timersRef.current.forEach((timer) => clearTimeout(timer));
      timersRef.current.clear();
    },
    []
  );

  const api = useMemo(
    () => ({
      show,
      dismiss,
      dismissAll,
      error: (message, options) => show(message, { ...options, type: 'error' }),
      success: (message, options) => show(message, { ...options, type: 'success' }),
      info: (message, options) => show(message, { ...options, type: 'info' }),
      warning: (message, options) => show(message, { ...options, type: 'warning' })
    }),
    [show, dismiss, dismissAll]
  );

  const visible = useMemo(
    () => toasts.map((item) => ({ ...item, dismissLabel: t('common.dismiss') })),
    [toasts, t]
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastRegion toasts={visible} onDismiss={dismiss} aria-label={t('a11y.notifications')} />
    </ToastContext.Provider>
  );
}

/**
 * @returns {{
 *   show: (message: import('react').ReactNode, options?: object) => number,
 *   error: (message: import('react').ReactNode, options?: object) => number,
 *   success: (message: import('react').ReactNode, options?: object) => number,
 *   info: (message: import('react').ReactNode, options?: object) => number,
 *   warning: (message: import('react').ReactNode, options?: object) => number,
 *   dismiss: (id: number) => void,
 *   dismissAll: () => void
 * }}
 */
export function useToast() {
  const ctx = useContext(ToastContext);
  // Rendering outside the provider must not crash a screen over a notification.
  return ctx ?? NOOP_TOAST;
}

const NOOP_TOAST = {
  show: () => 0,
  error: () => 0,
  success: () => 0,
  info: () => 0,
  warning: () => 0,
  dismiss: () => {},
  dismissAll: () => {}
};

export default ToastProvider;
